import type { TFunction } from 'i18next'
import api from './api'
import i18n from '../i18n'
import { resolveCheckoutLocale } from '../i18n/constants'

const SUB_NS = 'workspace'
const SP = 'settings.subscriptionPanel'
const LFT = 'lucasFreeTier'
const PE = 'paywall.errors'

export interface SubscriptionEntitlements {
  plan: string
  /** Stripe price tier for UI / CTAs (free|plus|pro). Prefer over is_pro for labels. */
  billing_plan?: string
  status: string
  is_plus: boolean
  is_pro?: boolean
  has_course_context?: boolean
  course_context_tier?: string
  pending_plan?: string | null
  trial_end?: string | null
  current_period_end?: string | null
  next_billing_at?: string | null
  cancel_at_period_end?: boolean
  /** admin_gift | stripe | system */
  source?: string
  /** gift | stripe | none */
  billing_provider?: string
  is_gift?: boolean
  is_perpetual?: boolean
  free_prompt_limit?: number | null
  free_prompts_used: number
  free_prompts_remaining?: number | null
  /** @deprecated use free_prompt_* */
  daily_prompt_limit?: number | null
  /** @deprecated use free_prompt_* */
  daily_prompts_used: number
  /** @deprecated use free_prompt_* */
  daily_prompts_remaining?: number | null
  max_courses?: number | null
  courses_used: number
  saved_learning_limit?: number | null
  saved_learning_used?: number
  saved_learning_remaining?: number | null
  review_build_daily_limit?: number
  can_export_review?: boolean
  stripe_configured: boolean
  subscriptions_enabled: boolean
  trial_days?: number
  intro_fee_cents?: number
  intro_period_days?: number
  plus_price_cents?: number
  pro_price_cents?: number
}

export function isGiftSubscription(
  entitlements: Pick<SubscriptionEntitlements, 'is_gift' | 'billing_provider' | 'source'> | null | undefined
): boolean {
  if (!entitlements) return false
  if (entitlements.is_gift === true) return true
  if (entitlements.billing_provider === 'gift') return true
  return (entitlements.source || '').toLowerCase() === 'admin_gift'
}

export interface SubscriptionMeResponse {
  entitlements: SubscriptionEntitlements
  stripe_customer_id?: string | null
}

export interface StripePaymentMethod {
  id: string
  brand: string
  last4: string
  exp_month?: number | null
  exp_year?: number | null
  is_default?: boolean
}

export interface StripeBillingAddress {
  line1?: string
  line2?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
}

export interface SubscriptionBillingInfo {
  has_stripe_customer: boolean
  email?: string | null
  name?: string | null
  payment_methods: StripePaymentMethod[]
  billing_address: StripeBillingAddress
  /** True when stored Stripe IDs belong to the other mode (test vs live). */
  mode_mismatch?: boolean
  mode_mismatch_message?: string | null
  requires_resubscribe?: boolean
}

function _detailLooksLikeModeMismatch(text: string): boolean {
  const lower = text.toLowerCase()
  return (
    lower.includes('billing needs to be set up again') ||
    lower.includes('sandbox billing') ||
    lower.includes('payment mode') ||
    lower.includes('similar object exists in test mode') ||
    lower.includes('similar object exists in live mode') ||
    lower.includes('live mode key was used') ||
    lower.includes('test mode key was used')
  )
}

export function isStripeModeMismatchError(error: unknown): boolean {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (detail && typeof detail === 'object' && detail !== null && 'code' in detail) {
    return (detail as { code?: string }).code === 'stripe_mode_mismatch'
  }
  if (typeof detail === 'string') {
    return _detailLooksLikeModeMismatch(detail)
  }
  if (detail && typeof detail === 'object' && detail !== null && 'message' in detail) {
    return _detailLooksLikeModeMismatch(String((detail as { message?: string }).message || ''))
  }
  return false
}

/** Always prefer i18n `fallback` — never surface sandbox/mode jargon from the API. */
export function stripeModeMismatchMessage(_error: unknown, fallback: string): string {
  return fallback
}

/** Strip mode/sandbox jargon if an API string leaks into a generic error path. */
export function sanitizeClientBillingMessage(message: string, safeFallback: string): string {
  const trimmed = (message || '').trim()
  if (!trimmed) return safeFallback
  if (_detailLooksLikeModeMismatch(trimmed)) return safeFallback
  const lower = trimmed.toLowerCase()
  if (
    lower.includes('sandbox') ||
    lower.includes('complimentary (gift)') ||
    lower.includes('gift plans are unaffected')
  ) {
    return safeFallback
  }
  return trimmed
}

export interface SubscriptionInvoice {
  id: string
  number?: string | null
  status: string
  amount_paid_cents: number
  amount_due_cents: number
  currency: string
  created_at: string
  period_start?: string | null
  period_end?: string | null
  invoice_pdf?: string | null
  hosted_invoice_url?: string | null
  description?: string | null
}

export interface PaywallError {
  code: string
  /** Raw API copy — do not show in UI; use `formatPaywallMessage` instead. */
  message?: string
  upgradePath: string
  promptLimit?: number
  maxCourses?: number
  savedLearningLimit?: number
}

function normalizeEntitlements(raw: SubscriptionEntitlements): SubscriptionEntitlements {
  const used = raw.free_prompts_used ?? raw.daily_prompts_used ?? 0
  const limit = raw.free_prompt_limit ?? raw.daily_prompt_limit ?? null
  const remaining =
    raw.free_prompts_remaining ??
    raw.daily_prompts_remaining ??
    (limit != null ? Math.max(0, limit - used) : null)
  const billingPlan = getBillingPlan(raw)
  return {
    ...raw,
    billing_plan: billingPlan,
    is_pro: billingPlan === 'pro',
    free_prompts_used: used,
    free_prompt_limit: limit,
    free_prompts_remaining: remaining,
    daily_prompts_used: used,
    daily_prompt_limit: limit,
    daily_prompts_remaining: remaining,
  }
}

/** Stripe billing tier for labels/CTAs — ignores grandfather course-context flags. */
export function getBillingPlan(
  entitlements: Pick<SubscriptionEntitlements, 'is_plus' | 'plan' | 'billing_plan' | 'is_pro'>
): 'free' | 'plus' | 'pro' {
  if (!entitlements.is_plus) return 'free'
  const raw = String(entitlements.billing_plan || entitlements.plan || '')
    .trim()
    .toLowerCase()
  if (raw === 'pro') return 'pro'
  if (raw === 'plus') return 'plus'
  // Legacy clients may only have is_pro without billing_plan after a Pro sync.
  if (entitlements.is_pro && raw !== 'plus') return 'pro'
  return 'plus'
}

/** Plus billed with legacy course context (grandfather) — feature flag, not plan name. */
export function hasLegacyCourseContext(entitlements: SubscriptionEntitlements): boolean {
  return (
    entitlements.is_plus === true &&
    getBillingPlan(entitlements) === 'plus' &&
    Boolean(entitlements.has_course_context)
  )
}

class SubscriptionService {
  async getMySubscription(refresh = true): Promise<SubscriptionMeResponse> {
    const response = await api.get<SubscriptionMeResponse>('/subscriptions/me', {
      params: { refresh },
    })
    return {
      ...response.data,
      entitlements: normalizeEntitlements(response.data.entitlements),
    }
  }

  async getSubscriptionInvoices(limit = 24): Promise<{
    invoices: SubscriptionInvoice[]
    mode_mismatch?: boolean
    mode_mismatch_message?: string | null
    requires_resubscribe?: boolean
  }> {
    const response = await api.get<{
      invoices: SubscriptionInvoice[]
      mode_mismatch?: boolean
      mode_mismatch_message?: string | null
      requires_resubscribe?: boolean
    }>('/subscriptions/invoices', {
      params: { limit },
    })
    return {
      invoices: response.data.invoices ?? [],
      mode_mismatch: Boolean(response.data.mode_mismatch),
      mode_mismatch_message: response.data.mode_mismatch_message ?? null,
      requires_resubscribe: Boolean(response.data.requires_resubscribe),
    }
  }

  async startCheckout(options?: {
    successUrl?: string
    cancelUrl?: string
    affiliateRef?: string | null
    plan?: 'plus' | 'pro'
    locale?: string | null
  }): Promise<string> {
    const locale = resolveCheckoutLocale(options?.locale ?? i18n.resolvedLanguage ?? i18n.language)
    const response = await api.post<{ url: string; session_id: string }>('/subscriptions/checkout', {
      success_url: options?.successUrl,
      cancel_url: options?.cancelUrl,
      skip_trial: false,
      plan: options?.plan || 'plus',
      affiliate_ref: options?.affiliateRef || undefined,
      locale,
    })
    const url = (response.data?.url || '').trim()
    if (!url || !/^https?:\/\//i.test(url)) {
      throw new Error('Checkout session did not return a valid Stripe Checkout URL')
    }
    return url
  }

  async changePlan(target: 'plus' | 'pro'): Promise<{
    success: boolean
    action: string
    plan: string
    pending_plan?: string | null
    current_period_end?: string | null
    message?: string
    entitlements: SubscriptionEntitlements
  }> {
    const response = await api.post('/subscriptions/change-plan', { target })
    return {
      ...response.data,
      entitlements: normalizeEntitlements(response.data.entitlements),
    }
  }

  async changeGiftPlan(target: 'plus' | 'pro'): Promise<{
    success: boolean
    action: string
    plan: string
    message?: string
    entitlements: SubscriptionEntitlements
  }> {
    const response = await api.post('/subscriptions/gift/change-plan', { target })
    return {
      ...response.data,
      entitlements: normalizeEntitlements(response.data.entitlements),
    }
  }

  async cancelGiftPlan(): Promise<{
    success: boolean
    action: string
    plan: string
    message?: string
    entitlements: SubscriptionEntitlements
  }> {
    const response = await api.post('/subscriptions/gift/cancel')
    return {
      ...response.data,
      entitlements: normalizeEntitlements(response.data.entitlements),
    }
  }

  async openBillingPortal(returnUrl?: string, flow?: 'cancel'): Promise<string> {
    const response = await api.post<{ url: string }>('/subscriptions/portal', {
      return_url: returnUrl,
      flow: flow ?? undefined,
    })
    return response.data.url
  }

  async getBillingInfo(): Promise<SubscriptionBillingInfo> {
    const response = await api.get<SubscriptionBillingInfo>('/subscriptions/billing')
    return {
      has_stripe_customer: Boolean(response.data?.has_stripe_customer),
      email: response.data?.email ?? null,
      name: response.data?.name ?? null,
      payment_methods: response.data?.payment_methods ?? [],
      billing_address: response.data?.billing_address ?? {},
      mode_mismatch: Boolean(response.data?.mode_mismatch),
      mode_mismatch_message: response.data?.mode_mismatch_message ?? null,
      requires_resubscribe: Boolean(response.data?.requires_resubscribe),
    }
  }

  async syncCheckoutSession(sessionId: string) {
    const response = await api.post('/subscriptions/sync-session', { session_id: sessionId })
    return response.data
  }

  parsePaywallError(error: unknown): PaywallError | null {
    const anyErr = error as {
      response?: { status?: number; data?: { detail?: unknown } }
      message?: string
    }
    if (anyErr?.response?.status !== 402) return null

    const detail = anyErr.response?.data?.detail
    if (detail && typeof detail === 'object') {
      const obj = detail as {
        code?: string
        message?: string
        upgrade_path?: string
        prompt_limit?: number
        max_courses?: number
        saved_learning_limit?: number
      }
      if (obj.code) {
        return {
          code: obj.code,
          message: obj.message,
          upgradePath: obj.upgrade_path || '/account-settings?tab=subscription',
          promptLimit: obj.prompt_limit,
          maxCourses: obj.max_courses,
          savedLearningLimit: obj.saved_learning_limit,
        }
      }
    }

    if (typeof detail === 'string' && detail.trim()) {
      return {
        code: 'paywall',
        message: detail,
        upgradePath: '/account-settings?tab=subscription',
      }
    }

    return {
      code: 'paywall',
      upgradePath: '/account-settings?tab=subscription',
    }
  }

  formatPaywallMessage(paywall: PaywallError, t: TFunction): string {
    switch (paywall.code) {
      case 'lucas_daily_limit':
      case 'lucas_free_limit':
        return t(`${PE}.lucasDailyLimit`, {
          ns: SUB_NS,
          limit: paywall.promptLimit ?? 3,
        })
      case 'course_limit':
        return t(`${PE}.courseLimit`, {
          ns: SUB_NS,
          limit: paywall.maxCourses ?? 1,
        })
      case 'saved_learning_limit':
        return t(`${PE}.savedLearningLimit`, {
          ns: SUB_NS,
          limit: paywall.savedLearningLimit ?? 10,
        })
      case 'review_export_plus_required':
        return t(`${PE}.reviewExportPlusRequired`, { ns: SUB_NS })
      case 'course_folder_upload_plus_required':
        return t(`${PE}.courseFolderUploadPlusRequired`, { ns: SUB_NS })
      default:
        return t(`${PE}.generic`, { ns: SUB_NS })
    }
  }

  formatPlanLabel(entitlements: SubscriptionEntitlements, t: TFunction): string {
    if (entitlements.is_plus) {
      const billing = getBillingPlan(entitlements)
      const isBillingPro = billing === 'pro'
      if (isGiftSubscription(entitlements)) {
        return t(isBillingPro ? `${SP}.planProGift` : `${SP}.planPlusGift`, { ns: SUB_NS })
      }
      const pendingPlus = entitlements.pending_plan === 'plus'
      if (entitlements.status === 'trialing') {
        return t(isBillingPro ? `${SP}.planProIntro` : `${SP}.planIntro`, { ns: SUB_NS })
      }
      if (entitlements.cancel_at_period_end) {
        return t(isBillingPro ? `${SP}.planProCanceling` : `${SP}.planCanceling`, { ns: SUB_NS })
      }
      if (pendingPlus && isBillingPro) {
        return t(`${SP}.planProDowngrading`, { ns: SUB_NS })
      }
      return t(isBillingPro ? `${SP}.planPro` : `${SP}.planPlus`, { ns: SUB_NS })
    }
    return t(`${SP}.planFree`, { ns: SUB_NS })
  }

  formatMonthlyPrice(cents: number): string {
    const amount = `$${Math.round(cents / 100)}`
    return i18n.t(`${SP}.pricePerMonth`, { ns: SUB_NS, price: amount })
  }

  formatPlusMainPrice(entitlements?: SubscriptionEntitlements): string {
    const cents = entitlements?.plus_price_cents ?? 1200
    return this.formatMonthlyPrice(cents)
  }

  formatProMainPrice(entitlements?: SubscriptionEntitlements): string {
    const cents = entitlements?.pro_price_cents ?? 2200
    return this.formatMonthlyPrice(cents)
  }

  formatIntroOffer(entitlements: SubscriptionEntitlements, t: TFunction, plan: 'plus' | 'pro' = 'plus'): string {
    const days = entitlements.intro_period_days ?? entitlements.trial_days ?? 10
    const feeCents = entitlements.intro_fee_cents ?? 100
    const fee = feeCents / 100
    const feeLabel = Number.isInteger(fee) ? `$${fee}` : `$${fee.toFixed(2)}`
    const monthlyCents =
      plan === 'pro'
        ? entitlements.pro_price_cents ?? 2200
        : entitlements.plus_price_cents ?? 1200
    const monthly = `$${Math.round(monthlyCents / 100)}`
    return t(plan === 'pro' ? `${SP}.introOfferPro` : `${SP}.introOffer`, {
      ns: SUB_NS,
      fee: feeLabel,
      days,
      price: monthly,
    })
  }

  formatStatusLabel(entitlements: SubscriptionEntitlements, t: TFunction): string {
    if (isGiftSubscription(entitlements) && entitlements.is_plus) {
      return t(`${SP}.statusGiftActive`, { ns: SUB_NS })
    }
    if (entitlements.is_plus && entitlements.cancel_at_period_end) {
      const until = this.formatNextBillingDate(entitlements)
      return until
        ? t(`${SP}.statusActiveCanceling`, { ns: SUB_NS, date: until })
        : t(`${SP}.statusActiveCancelingNoDate`, { ns: SUB_NS })
    }
    const status = entitlements.status
    const labels: Record<string, string> = {
      free: t(`${SP}.statusFree`, { ns: SUB_NS }),
      trialing: t(`${SP}.statusIntro`, { ns: SUB_NS }),
      active: t(`${SP}.statusActive`, { ns: SUB_NS }),
      past_due: t(`${SP}.statusPastDue`, { ns: SUB_NS }),
      canceled: t(`${SP}.statusCanceled`, { ns: SUB_NS }),
      unpaid: t(`${SP}.statusUnpaid`, { ns: SUB_NS }),
    }
    return labels[status] || status
  }

  formatFreePromptMeter(entitlements: SubscriptionEntitlements, t: TFunction): string | null {
    if (entitlements.is_plus) return null
    const limit = entitlements.free_prompt_limit
    if (limit == null) return null
    const used = entitlements.free_prompts_used ?? 0
    const remaining = entitlements.free_prompts_remaining ?? Math.max(0, limit - used)
    if (remaining <= 0) return t(`${LFT}.noQuestionsLeft`, { ns: SUB_NS })
    return t(`${LFT}.questionsRemaining`, { ns: SUB_NS, remaining, limit })
  }

  formatNextBillingLabel(entitlements: SubscriptionEntitlements, t: TFunction): string | null {
    if (isGiftSubscription(entitlements)) {
      if (entitlements.is_perpetual || !entitlements.current_period_end) {
        return t(`${SP}.giftAccessLabel`, { ns: SUB_NS })
      }
      return t(`${SP}.accessUntil`, { ns: SUB_NS })
    }
    const hasDate = entitlements.next_billing_at || entitlements.current_period_end
    if (!hasDate) return null
    if (entitlements.cancel_at_period_end) return t(`${SP}.accessUntil`, { ns: SUB_NS })
    if (entitlements.status === 'trialing') return t(`${SP}.plusStartsOn`, { ns: SUB_NS })
    return t(`${SP}.nextPayment`, { ns: SUB_NS })
  }

  formatNextBillingDate(entitlements: SubscriptionEntitlements): string | null {
    if (isGiftSubscription(entitlements)) {
      if (entitlements.is_perpetual || !entitlements.current_period_end) {
        return null
      }
      return new Date(entitlements.current_period_end).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    }
    const raw = entitlements.next_billing_at || entitlements.current_period_end
    if (!raw) return null
    return new Date(raw).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  formatInvoiceAmount(invoice: SubscriptionInvoice): string {
    const cents = invoice.amount_paid_cents > 0 ? invoice.amount_paid_cents : invoice.amount_due_cents
    const amount = cents / 100
    const currency = (invoice.currency || 'usd').toUpperCase()
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: invoice.currency || 'usd',
      }).format(amount)
    } catch {
      return `${currency} ${amount.toFixed(2)}`
    }
  }

  formatInvoiceStatus(status: string, t: TFunction): string {
    const labels: Record<string, string> = {
      paid: t(`${SP}.invoicePaid`, { ns: SUB_NS }),
      open: t(`${SP}.invoiceOpen`, { ns: SUB_NS }),
      draft: t(`${SP}.invoiceDraft`, { ns: SUB_NS }),
      void: t(`${SP}.invoiceVoid`, { ns: SUB_NS }),
      uncollectible: t(`${SP}.invoiceUncollectible`, { ns: SUB_NS }),
    }
    return labels[status] || status.replace(/_/g, ' ')
  }
}

export const subscriptionService = new SubscriptionService()

export function subscriptionPortalReturnUrl(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.zimark.org'
  return `${origin}/account-settings?tab=subscription&portal=returned`
}

export function subscriptionCheckoutSuccessUrl(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.zimark.org'
  return `${origin}/account-settings?tab=subscription&session_id={CHECKOUT_SESSION_ID}`
}

export function subscriptionCheckoutCancelUrl(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.zimark.org'
  return `${origin}/account-settings?tab=subscription&checkout=canceled`
}
